/**
 * Conteo de días hábiles.
 *
 * Es el cálculo del que cuelga la fecha límite de pago de cada tarjeta, así que
 * un error aquí se traduce directamente en un recargo del banco.
 */

import {
  type PlainDate,
  addDays,
  isWeekend,
  toEpochDay,
} from './plainDate.js'

/** Conjunto de fechas inhábiles en formato `YYYY-MM-DD`. */
export type HolidaySet = ReadonlySet<string>

export const NO_HOLIDAYS: HolidaySet = new Set<string>()

/** Qué hacer cuando una fecha programada cae en día inhábil. */
export type NonBusinessDayRule = 'PREVIOUS_BUSINESS_DAY' | 'NEXT_BUSINESS_DAY' | 'EXACT'

export function isBusinessDay(date: PlainDate, holidays: HolidaySet = NO_HOLIDAYS): boolean {
  return !isWeekend(date) && !holidays.has(date)
}

/**
 * Tope de seguridad. Un año tiene ~240 días hábiles, así que N días hábiles
 * ocupan como mucho ~1.6·N naturales; el triple más un mes de holgura absorbe
 * cualquier racha realista de festivos. Existe para que un calendario mal
 * cargado falle con un error legible en vez de colgar el proceso.
 */
function iterationLimit(count: number): number {
  return Math.abs(count) * 3 + 30
}

/**
 * Suma días hábiles. La fecha de partida **no** se cuenta, tenga o no
 * actividad bancaria: «20 días hábiles para pagar» empieza a contar el día
 * siguiente al corte.
 *
 * Con `count` negativo cuenta hacia atrás. Con `count` cero devuelve la misma
 * fecha sin ajustarla — para eso está `adjustToBusinessDay`.
 */
export function addBusinessDays(
  date: PlainDate,
  count: number,
  holidays: HolidaySet = NO_HOLIDAYS,
): PlainDate {
  if (!Number.isInteger(count)) {
    throw new RangeError(`El número de días hábiles debe ser entero: ${count}`)
  }
  if (count === 0) return date

  const step = count > 0 ? 1 : -1
  const limit = iterationLimit(count)
  let remaining = Math.abs(count)
  let cursor = date
  let iterations = 0

  while (remaining > 0) {
    cursor = addDays(cursor, step)
    if (isBusinessDay(cursor, holidays)) {
      remaining -= 1
    }
    if (++iterations > limit) {
      throw new Error(
        `No se pudo resolver ${count} días hábiles desde ${date}: revisa el calendario de festivos`,
      )
    }
  }

  return cursor
}

/** Primer día hábil estrictamente posterior. */
export function nextBusinessDay(date: PlainDate, holidays: HolidaySet = NO_HOLIDAYS): PlainDate {
  return addBusinessDays(date, 1, holidays)
}

/** Primer día hábil estrictamente anterior. */
export function previousBusinessDay(date: PlainDate, holidays: HolidaySet = NO_HOLIDAYS): PlainDate {
  return addBusinessDays(date, -1, holidays)
}

/**
 * Mueve una fecha al día hábil correspondiente según la regla.
 * A diferencia de `addBusinessDays`, si la fecha ya es hábil la deja intacta.
 *
 * `PREVIOUS_BUSINESS_DAY` es el valor por defecto para nóminas: en México lo
 * habitual es adelantar el pago cuando la quincena cae en fin de semana.
 */
export function adjustToBusinessDay(
  date: PlainDate,
  rule: NonBusinessDayRule,
  holidays: HolidaySet = NO_HOLIDAYS,
): PlainDate {
  if (rule === 'EXACT' || isBusinessDay(date, holidays)) return date
  return rule === 'PREVIOUS_BUSINESS_DAY'
    ? previousBusinessDay(date, holidays)
    : nextBusinessDay(date, holidays)
}

/**
 * Días hábiles en un rango, contando ambos extremos.
 * Es la operación inversa de `addBusinessDays` y sirve para verificarla.
 */
export function countBusinessDays(
  start: PlainDate,
  end: PlainDate,
  holidays: HolidaySet = NO_HOLIDAYS,
): number {
  if (start > end) return 0
  let count = 0
  const last = toEpochDay(end)
  let cursor = start
  for (let d = toEpochDay(start); d <= last; d++) {
    if (isBusinessDay(cursor, holidays)) count += 1
    cursor = addDays(cursor, 1)
  }
  return count
}

/**
 * Días hábiles que faltan desde `from` hasta `target`, sin contar `from`.
 * Alimenta las cuentas regresivas de «vence en N días hábiles».
 */
export function businessDaysUntil(
  from: PlainDate,
  target: PlainDate,
  holidays: HolidaySet = NO_HOLIDAYS,
): number {
  if (target <= from) return 0
  return countBusinessDays(addDays(from, 1), target, holidays)
}
